import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

import { TASK_COLORS } from 'components/tasks/taskColors.js'
import { PRIORITY_ORDER } from 'components/tasks/taskPriorities.js'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes, matching the server-side cache
const FILTERS_KEY = 'tasks_stats_filters'
const RANGES = ['7d', '30d', '90d', '12m', 'custom']
const TASK_TYPES = ['all', 'one_time', 'recurring']
const DEFAULT_RANGE = '30d'

const VALID_COLORS = TASK_COLORS.map(c => c.value)

function defaultFilters() {
  return {
    range: DEFAULT_RANGE,
    startDate: '',
    endDate: '',
    priorities: [],
    colors: [],
    taskType: 'all',
    includeCanceled: false,
  }
}

// Restored filters are the one input to this store that nobody controls: they
// were written by a previous session, possibly months ago, possibly by hand,
// possibly naming a colour that has since left the palette. Every field is
// checked against the values that actually exist rather than trusted.
//
// The server tolerates junk values (it intersects them away too), so this is
// not about avoiding an error. It is about the controls matching the data: a
// stale colour would otherwise render as a selected filter chip with a count,
// pointing at a swatch that no longer appears in the dropdown, and no way to
// deselect it short of clearing site data.
function sanitizeFilters(raw) {
  const clean = defaultFilters()
  if (!raw || typeof raw !== 'object') return clean

  if (RANGES.includes(raw.range)) clean.range = raw.range
  if (TASK_TYPES.includes(raw.taskType)) clean.taskType = raw.taskType
  clean.includeCanceled = raw.includeCanceled === true

  clean.priorities = Array.isArray(raw.priorities)
    ? raw.priorities.filter(p => PRIORITY_ORDER.includes(p))
    : []
  clean.colors = Array.isArray(raw.colors)
    ? raw.colors.filter(c => VALID_COLORS.includes(c))
    : []

  // A custom range is only restorable with both ends intact. Half a range
  // would leave the page sitting on its "pick a start and end date" state with
  // no visible reason why.
  const datesLookRight = typeof raw.startDate === 'string' && typeof raw.endDate === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(raw.startDate) && /^\d{4}-\d{2}-\d{2}$/.test(raw.endDate)
  if (clean.range === 'custom' && datesLookRight) {
    clean.startDate = raw.startDate
    clean.endDate = raw.endDate
  } else if (clean.range === 'custom') {
    clean.range = DEFAULT_RANGE
  }

  return clean
}

// The whole filter set persists, not just the range, so coming back to Stats
// picks up where you left off the way Checklist remembers its chip and
// TasksPage remembers its tab.
//
// The usual objection to sticky filters is an invisible one leaving someone
// staring at an empty dashboard. That does not apply here: an active filter
// shows as a filled blue chip carrying its own count, a Reset button appears
// beside them, and the empty state says "No tasks match these filters" rather
// than pretending the account is empty.
function storedFilters() {
  try {
    return sanitizeFilters(JSON.parse(localStorage.getItem(FILTERS_KEY)))
  } catch {
    return defaultFilters()
  }
}

function persistFilters(filters) {
  try {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters))
  } catch {
    // Private mode or blocked site data. Remembering filters is a
    // convenience, never a reason to break the page.
  }
}

export const useTaskStatsStore = defineStore('taskStats', {
  state: () => ({
    stats: null,
    loading: false,
    error: null,
    filters: storedFilters(),
    // Keyed by query string, so flipping back to a range you already looked at
    // is instant rather than another round trip.
    cache: {},
  }),

  getters: {
    // The query string doubles as the cache key: it is exactly the set of
    // inputs that changes the answer.
    queryString: (state) => {
      const f = state.filters
      const params = new URLSearchParams()
      params.set('range', f.range)
      if (f.range === 'custom') {
        params.set('start_date', f.startDate)
        params.set('end_date', f.endDate)
      }
      f.priorities.forEach(p => params.append('priority[]', p))
      f.colors.forEach(c => params.append('color[]', c))
      if (f.taskType !== 'all') params.set('task_type', f.taskType)
      if (f.includeCanceled) params.set('include_canceled', 'true')
      return params.toString()
    },

    // A custom range with only one date chosen is not yet a question worth
    // asking the server.
    isRangeComplete: (state) =>
      state.filters.range !== 'custom' || (!!state.filters.startDate && !!state.filters.endDate),

    hasActiveFilters: (state) => {
      const f = state.filters
      return f.priorities.length > 0 || f.colors.length > 0 || f.taskType !== 'all' || f.includeCanceled
    },

    // Distinguishes "no tasks at all" from "none match these filters", which
    // need very different empty states.
    isEmpty: (state) => {
      if (!state.stats) return false
      const s = state.stats
      return s.kpis.completed.value === 0 &&
        s.by_status.every(b => b.count === 0) &&
        s.longest_open.length === 0 &&
        s.over_time.every(b => b.created === 0 && b.completed === 0)
    },
  },

  actions: {
    async fetchIfStale() {
      const key = this.queryString
      const hit = this.cache[key]
      if (hit && Date.now() - hit.fetchedAt < CACHE_TTL) {
        this.stats = hit.data
        this.error = null
        return
      }
      await this.fetchStats()
    },

    async fetchStats() {
      if (!this.isRangeComplete) return

      const key = this.queryString
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/tasks/stats?${key}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.stats = res.response.data
          this.cache[key] = { data: res.response.data, fetchedAt: Date.now() }
        } else {
          this.error = res.response?.message || 'Failed to load stats.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    // Applies filters that came from the URL. Deliberately neither persists
    // nor fetches: it runs before the page mounts, so the component's own
    // `fetchIfStale` picks these up a moment later, and writing them to
    // localStorage would mean opening a colleague's link quietly replaced
    // your own saved filters with theirs.
    hydrateFilters(patch) {
      if (!patch) return
      Object.assign(this.filters, sanitizeFilters({ ...this.filters, ...patch }))
    },

    // Every filter change goes through here so the refetch can never be
    // forgotten at a call site.
    async setFilters(patch) {
      Object.assign(this.filters, patch)
      persistFilters(this.filters)
      await this.fetchIfStale()
    },

    async resetFilters() {
      const range = this.filters.range
      const startDate = this.filters.startDate
      const endDate = this.filters.endDate
      // Clears the dimension filters but keeps the window, which is what
      // "reset" means when you are staring at an over-filtered empty page.
      this.filters = { ...defaultFilters(), range, startDate, endDate }
      persistFilters(this.filters)
      await this.fetchIfStale()
    },

    invalidate() {
      this.cache = {}
    },
  },
})
